import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, PrimaryButton, SecondaryButton, FormSelect, CircularLoader, FeedbackMessage } from "@husqvarna/ui-core";
import { FormSelectOption } from "@husqvarna/ui-core/dist/components/form-select";

import { useCheckAuthQuery } from "../shared/api/authApi";
import { useGetRegionsQuery, useGetAdminProductsQuery, useSaveAdminProductsMutation } from "./adminApi";
import FormTextWithSuffix from "../shared/components/FormTextWithSuffix";
import CreateRegionDialog from "./components/CreateRegionDialog";
import "./AdminPage.scss";

interface ProductFormItem {
    productId: string;
    isActive: boolean;
    price: string;
}

interface AdminFormValues {
    products: ProductFormItem[];
}

const AdminPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { data: user } = useCheckAuthQuery();
    const { data: regions } = useGetRegionsQuery();
    const [selectedRegionId, setSelectedRegionId] = useState<string>(
        () => window.location.hash.replace('#', '') || ""
    );
    const [showCreateRegion, setShowCreateRegion] = useState(false);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { data: products, isLoading: productsLoading } = useGetAdminProductsQuery(selectedRegionId, {
        skip: !selectedRegionId
    });

    const [saveProducts, { isLoading: isSaving }] = useSaveAdminProductsMutation();

    const { register, control, handleSubmit, reset, watch, setValue } = useForm<AdminFormValues>({
        defaultValues: { products: [] }
    });

    const { fields } = useFieldArray({ control, name: "products", keyName: "fieldId" });

    useEffect(() => {
        if (regions?.length && !selectedRegionId) {
            setSelectedRegionId(regions[0]!.id);
        }
    }, [regions, selectedRegionId]);

    useEffect(() => {
        if (selectedRegionId) {
            window.location.hash = selectedRegionId;
        }
    }, [selectedRegionId]);

    useEffect(() => {
        reset({ products: [] });
    }, [selectedRegionId, reset]);

    useEffect(() => {
        if (products) {
            reset({
                products: products.map(p => ({
                    productId: p.productId,
                    isActive: p.isActive,
                    price: p.price.toString()
                }))
            });
        }
    }, [products, reset]);

    if (!user?.isAdmin) {
        return <Navigate replace to="/projects" />;
    }

    const regionOptions: FormSelectOption[] = regions?.map(r => ({
        label: r.name,
        value: r.id
    })) ?? [];

    const selectedRegion = regions?.find(r => r.id === selectedRegionId);

    const showFeedback = (message: string) => {
        setFeedback(message);
        setTimeout(() => setFeedback(null), 3000);
    };

    const onSubmit = async (data: AdminFormValues) => {
        if (!selectedRegionId) return;
        setFeedback(null);
        setError(null);
        const result = await saveProducts({
            regionId: selectedRegionId,
            products: data.products.map(p => ({
                productId: p.productId,
                isActive: p.isActive,
                price: parseFloat(p.price) || 0
            }))
        });
        if ('error' in result) {
            const err = result.error as any;
            const message = typeof err?.data === 'string' ? err.data : "Save failed.";
            setError(message);
        } else {
              const updated = result.data;
            reset({
                products: updated.map(p => ({
                    productId: p.productId,
                    isActive: p.isActive,
                    price: p.price.toString()
                }))
            });
            showFeedback(intl.formatMessage({ id: "admin.saveSuccess", defaultMessage: "Changes saved successfully" }));
        }
    };

    const handleRegionChange = (e: any) => {
        setSelectedRegionId(e.target.value);
        setFeedback(null);
    };

    return (
        <Box className="admin-page" flexDirection="column" width="full">
            <Box className="admin-toolbar" alignItems="center" justifyContent="space-between" px={6} py={4}>
                <Box alignItems="center" gap={3}>
                    <SecondaryButton
                        onClick={() => navigate("/projects")}
                        text={intl.formatMessage({ id: "admin.backToProjects", defaultMessage: "Back to projects" })}
                    />
                    {regions && (
                        <div style={{ width: '300px' }}>
                            <FormSelect
                                width="auto"
                                label={intl.formatMessage({ id: "admin.selectRegion", defaultMessage: "Region" })}
                                options={regionOptions}
                                selectedValue={selectedRegionId}
                                onChangeListener={handleRegionChange}
                            />
                        </div>
                    )}
                </Box>
                <SecondaryButton
                    onClick={() => setShowCreateRegion(true)}
                    text={intl.formatMessage({ id: "admin.createRegion", defaultMessage: "Create region" })}
                />
            </Box>

            <Box className="admin-content" flexDirection="column" px={6} py={4}>
                {feedback && (
                    <Box className="admin-feedback">
                        <FeedbackMessage variant="SuccessGreen" message={feedback} />
                    </Box>
                )}
                {error && (
                    <Box className="admin-feedback">
                        <FeedbackMessage variant="ErrorRed" message={error} />
                    </Box>
                )}
                <Typography tag="h1" variant="Heading1" mb={4}>
                    <FormattedMessage id="admin.heading" defaultMessage="Product administration" />
                </Typography>

                {productsLoading && (
                    <Box justifyContent="center" py={8}>
                        <CircularLoader />
                    </Box>
                )}

                {!productsLoading && products && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box className="admin-product-list" flexDirection="column" gap={2}>
                            <Box className="admin-product-header" alignItems="center" gap={3} px={3} py={2}>
                                <Box className="admin-col-checkbox" />
                                <Box flexGrow={1}>
                                    <Typography variant="Heading4">
                                        <FormattedMessage id="admin.productName" defaultMessage="Product" />
                                    </Typography>
                                </Box>
                                <Box className="admin-col-price">
                                    <Typography variant="Heading4">
                                        <FormattedMessage id="admin.price" defaultMessage="Price" />
                                    </Typography>
                                </Box>
                            </Box>

                            {fields.map((field, index) => {
                                const product = products[index];
                                if (!product) return null;

                                return (
                                    <Box key={field.fieldId} className="admin-product-row" alignItems="center" gap={3} px={3} py={2}>
                                        <div className="admin-col-checkbox" style={{ display: 'flex', justifyContent: 'center' }}
                                            title={product.isActive ? intl.formatMessage({ id: "admin.removeNotSupported", defaultMessage: "Removal of Product from Region is not supported" }) : undefined}
                                        >
                                            <input
                                                type="checkbox"
                                                className="admin-checkbox"
                                                {...register(`products.${index}.isActive`)}
                                                disabled={product.isActive}
                                            />
                                        </div>
                                        <Box flexGrow={1} flexDirection="column">
                                            <Typography variant="Body1">{product.name}</Typography>
                                            <Typography variant="Microcopy" color="Grey500">
                                                {product.articleNumber} — {product.productType}
                                            </Typography>
                                        </Box>
                                        <Box className="admin-col-price">
                                            <FormTextWithSuffix
                                                {...register(`products.${index}.price`, {
                                                    onChange: (e) => {
                                                        e.target.value = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').replace(/(\.\d{2})\d+/, '$1').replace(/^(\d{16})\d+/, '$1');
                                                        const val = e.target.value.trim();
                                                        setValue(`products.${index}.isActive`, val !== '' && val !== '0');
                                                    }
                                                })}
                                                value={watch(`products.${index}.price`)}
                                                suffix={selectedRegion?.currency}
                                                fitContent={false}
                                            />
                                        </Box>
                                        <input type="hidden" {...register(`products.${index}.productId`)} />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box mt={6} gap={3} alignItems="center" justifyContent="flex-end">
                            <PrimaryButton
                                submit
                                text={intl.formatMessage({ id: "admin.save", defaultMessage: "Save changes" })}
                                disabled={isSaving}
                                loading={isSaving}
                            />
                        </Box>
                    </form>
                )}
            </Box>

            {showCreateRegion && (
                <CreateRegionDialog
                    regions={regions}
                    onClose={() => setShowCreateRegion(false)}
                    onCreated={(regionId) => {
                        setShowCreateRegion(false);
                        setSelectedRegionId(regionId);
                        showFeedback(intl.formatMessage({ id: "admin.regionCreateSuccess", defaultMessage: "Region created successfully" }));
                    }}
                />
            )}
        </Box>
    );
};

export default AdminPage;
